import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import FundraiserBodyHeader from "../../User/common/FundraiserBodyHeader";
import Loader from "../../common/Loader";
import toast from "../../../utils/toast";
import { setToken } from "../../../utils/connectStore";
import {
  getUserSubscription,
  getSubscriptionPlans,
  renewSubscriptionFromReminder,
} from "../../../services/subscriptionService";
import { verifyMagicCode } from "../../../services/authService";
import FundraiserHeader from "../common/FundraiserHeader";

const UserSubscription = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [yearlyPlan, setYearlyPlan] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshing = useSelector(
    (state) => state.subscriptionReducer.refreshing,
  );
  const tokenData = useSelector((state) => state.authReducer?.tokenData);
  const token = tokenData?.token;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchPlans = async () => {
    try {
      const response = await dispatch(getSubscriptionPlans({})).unwrap();
      const plans = response?.data || [];
      const pickYearly =
        plans.find((p) =>
          String(p?.duration || "")
            .toLowerCase()
            .includes("year"),
        ) ||
        plans[0] ||
        null;
      setYearlyPlan(pickYearly);
    } catch {
      toast.error("Failed to load membership details.");
    }
  };

  const fetchUserSubscription = async () => {
    try {
      const result = await dispatch(getUserSubscription()).unwrap();
      const sub =
        Array.isArray(result?.data) && result.data.length > 0
          ? result.data[0]
          : null;
      setActiveSub(sub);
    } catch (err) {
      console.warn("fetchUserSubscription failed:", err);
      setActiveSub(null);
    }
  };

  useEffect(() => {
    const init = async () => {
      const code = searchParams.get("code");
      const urlToken = searchParams.get("token");

      if (code) {
        try {
          await dispatch(verifyMagicCode({ code })).unwrap();
        } catch (err) {
          console.error("verifyMagicCode failed:", err);
          toast.error(err?.message || "Invalid or expired link code.");
        }
      } else if (urlToken) {
        try {
          setToken(urlToken);
        } catch (err) {
          console.warn("setToken failed:", err);
        }
      }

      await fetchPlans();
      await fetchUserSubscription();
      setIsLoading(false);
    };

    init();
  }, [dispatch, searchParams]);

  const handleOpenMembership = () => {
    navigate("/swoodleherosection");
  };

  const isActive = activeSub?.status === "active";
  const canRenewToday = activeSub?.canRenewToday === true;
  const isReminderWindow = activeSub?.isExpiryReminderWindow === true;

  const handleReminderRenewal = async () => {
    try {
      const renewPayload = {
        priceId: activeSub?.priceId || yearlyPlan?.priceId,
      };
      const renewResult = await dispatch(
        renewSubscriptionFromReminder(renewPayload),
      ).unwrap();

      if (renewResult?.success && renewResult?.data?.url) {
        window.location.href = renewResult.data.url;
      }
    } catch (error) {
      console.error("Expiry-day renewal failed:", error);
      toast.error("Unable to start renewal right now.");
    }
  };

  return (
    <>
      <FundraiserHeader />
      <div className="bg-[#F4F4F4]">
        <div className="w-full h-full ">
          {isLoading || refreshing ? <Loader /> : null}

          {!isLoading && (
            <div className="bg-[#F4F4F4] py-8 flex flex-col items-center gap-12">
              <FundraiserBodyHeader
                heading="Your Membership"
                contentClass="w-[100%] sm:w-[80%] md:w-[90%] text-[#8C8C8C]"
              />

              <div className="flex flex-col w-full items-center justify-center gap-5 px-6">
                <div className="bg-gradient-to-br from-[#00ADEF] to-[#00ADEF] shadow-xl text-white rounded-2xl p-6 max-w-md w-full relative overflow-hidden">
                  <div className="absolute right-4 top-4 text-xs bg-white text-[#0076A3] px-3 py-1 rounded-full">
                    {activeSub?.status?.toUpperCase() || "INACTIVE"}
                  </div>

                  {isActive ? (
                    <>
                      <h2 className="text-2xl font-semibold">
                        {activeSub?.name ||
                          yearlyPlan?.name ||
                          "Membership Plan"}
                      </h2>

                      <p className="mt-2 text-sm opacity-90 leading-relaxed">
                        {activeSub?.description || yearlyPlan?.description}
                      </p>

                      <div className="mt-6 bg-white/20 rounded-xl p-4 backdrop-blur-md text-sm">
                        <p>
                          Duration: 1{" "}
                          <span className="font-semibold">
                            {activeSub?.duration || yearlyPlan?.duration}
                          </span>
                        </p>

                        <p>
                          Start:{" "}
                          <span className="font-semibold">
                            {formatDate(activeSub?.subscriptionStartTime)}
                          </span>
                        </p>

                        <p>
                          Expiry:{" "}
                          <span className="font-semibold">
                            {formatDate(activeSub?.subscriptionExpiryTime)}
                          </span>
                        </p>
                      </div>

                      <p className="text-xs opacity-75 mt-4 mb-2">
                        40% of your Membership fee goes directly towards your
                        chosen Fundraiser.
                      </p>

                      {canRenewToday ? (
                        <button
                          onClick={handleReminderRenewal}
                          className="mt-4 bg-white text-[#0076A3] font-semibold px-5 py-2.5 rounded-full hover:bg-gray-100 transition"
                        >
                          Renew Now
                        </button>
                      ) : isReminderWindow ? (
                        <button
                          onClick={handleOpenMembership}
                          className="mt-4 bg-white text-[#0076A3] font-semibold px-5 py-2.5 rounded-full hover:bg-gray-100 transition"
                        >
                          Manage Membership
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-center py-10">
                      <h2 className="text-xl font-semibold mb-2">
                        Membership Inactive
                      </h2>
                      <p className="text-sm opacity-90 leading-relaxed mb-6">
                        Activate your membership to view full plan details,
                        benefits, and support the foundation.
                      </p>
                      <button
                        onClick={handleOpenMembership}
                        className="bg-white text-[#0076A3] font-medium px-4 py-2 rounded-full hover:bg-gray-100 transition"
                      >
                        Activate Membership
                      </button>
                    </div>
                  )}
                </div>

                {/* <div className="text-center text-sm text-gray-500 mt-4">
                  To manage, renew, or cancel your membership, visit Swoodle website.
                  <br />
                  <button
                    onClick={handleOpenMembership}
                    className="text-[#00ADEF] underline text-sm mt-1"
                  >
                    Manage Your Membership
                  </button>
                </div> */}
                <div className="text-center text-sm text-gray-500 mt-4">
                  To manage, renew, or cancel your membership,{" "}
                  <button
                    onClick={handleOpenMembership}
                    className="text-[#00ADEF] underline"
                  >
                    visit Your Membership
                  </button>
                  .
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserSubscription;
